// src/pages/AI/AI.jsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";

// ---- CLARITY helper ----
const track = (name, props = {}) => {
  if (typeof window !== "undefined" && window.clarity) {
    window.clarity("event", name, props);
  }
};

const AI = ({ openContactModal }) => {
  useEffect(() => {
    track("ai_page_view", { path: "/ai" });
  }, []);

  const requestPilot = (placement = "ai") => {
    track("pilot_request_click", { placement, intent: "ai_pilot" });
    if (openContactModal) openContactModal();
  };

  return (
    <main className="bg-white">
      {/* HERO */}
      <section className="px-6 lg:px-20 pt-10 pb-12">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <div className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full text-xs font-semibold">
              🤖 AI & Innovation
              <span className="h-1 w-1 rounded-full bg-gray-400" />
              Human-in-the-loop
            </div>

            <h1 className="mt-4 text-3xl sm:text-4xl font-extrabold text-gray-900 leading-tight">
              AI-Ready Estimation Platform for Faster, Smarter Workflows
            </h1>

            <p className="mt-4 text-gray-700 text-base sm:text-lg leading-relaxed">
              Our roadmap focuses on intelligent assistance—helping professionals reduce manual effort while keeping
              outputs compliant, explainable, and audit-friendly.
            </p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Link
                to="/ai/roadmap"
                onClick={() => track("nav_click", { item: "ai_roadmap", placement: "ai_hero" })}
                className="w-full inline-flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-full font-semibold text-sm shadow-sm transition"
              >
                View Roadmap
              </Link>

              <button
                type="button"
                onClick={() => requestPilot("ai_hero")}
                className="w-full inline-flex items-center justify-center bg-white border border-gray-200 hover:bg-gray-50 text-gray-900 px-5 py-2.5 rounded-full font-semibold text-sm transition"
              >
                Join Pilot
              </button>

              <Link
                to="/features"
                onClick={() => track("nav_click", { item: "features", placement: "ai_hero" })}
                className="w-full inline-flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-900 px-5 py-2.5 rounded-full font-semibold text-sm transition"
              >
                Explore Platform
              </Link>
            </div>

            <p className="mt-4 text-xs text-gray-500">
              myBOQ is a product by <span className="font-semibold text-gray-700">SiliconMount Tech Services Pvt. Ltd.</span>{" "}
              (DPIIT-recognized Startup under Startup India).
            </p>
          </div>

          <div className="space-y-4">
            <div className="rounded-2xl border border-gray-200 bg-gray-50 shadow-sm overflow-hidden">
              <img
                src="/hero-infra.png"
                alt="AI roadmap for estimation"
                className="w-full h-[260px] sm:h-[320px] lg:h-[360px] object-cover"
                loading="eager"
                onError={(e) => {
                  e.currentTarget.src = "/ai_recom.png";
                  e.currentTarget.className = "w-full h-[260px] sm:h-[320px] lg:h-[360px] object-contain p-10 bg-white";
                }}
              />
              <div className="p-5 bg-white">
                <div className="text-sm font-bold text-gray-900">AI with accountability</div>
                <div className="text-sm text-gray-600 mt-1">
                  Suggestions are explainable, editable, and designed to keep professionals in control.
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="text-xs font-semibold bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                    Explainable suggestions
                  </span>
                  <span className="text-xs font-semibold bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                    Audit-friendly
                  </span>
                  <span className="text-xs font-semibold bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                    Human review
                  </span>
                </div>
              </div>
            </div>

        
          </div>
        </div>
      </section>
{/* PILOT OUTCOMES — FULL WIDTH, ALIGNED TO PAGE CONTAINER */}
<section className="py-12 bg-white border-t border-gray-200">
  <div className="px-6 lg:px-20">
    <div className="max-w-7xl mx-auto">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <h3 className="text-2xl font-extrabold text-gray-900">
            Pilot outcomes we target
          </h3>
          <p className="mt-2 text-sm sm:text-base text-gray-600 max-w-3xl">
            Clear, measurable improvements for estimation speed, consistency, and compliance.
          </p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-gray-200 rounded-xl px-5 py-4 bg-gray-50">
          <div className="text-sm font-bold text-gray-900">Faster estimation</div>
          <p className="mt-1 text-sm text-gray-700 leading-relaxed">
            Reduce estimation time through guided item suggestions and smart defaults.
          </p>
        </div>

        <div className="border border-gray-200 rounded-xl px-5 py-4 bg-gray-50">
          <div className="text-sm font-bold text-gray-900">Higher consistency</div>
          <p className="mt-1 text-sm text-gray-700 leading-relaxed">
            Improve uniformity across engineers, teams, and departments with standard outputs.
          </p>
        </div>

        <div className="border border-gray-200 rounded-xl px-5 py-4 bg-gray-50">
          <div className="text-sm font-bold text-gray-900">Fewer misses</div>
          <p className="mt-1 text-sm text-gray-700 leading-relaxed">
            Detect missing steps before report generation to reduce rework and approval delays.
          </p>
        </div>
      </div>
    </div>
  </div>
</section>

      {/* ROADMAP CARDS */}
      <section className="px-6 lg:px-20 py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">AI & Innovation Roadmap</h2>
              <p className="mt-2 text-gray-600 text-sm sm:text-base max-w-3xl">
                Focused on decision support, consistency, and faster workflows—without compromising compliance.
              </p>
            </div>
            <Link
              to="/ai/roadmap"
              onClick={() => track("nav_click", { item: "ai_roadmap", placement: "ai_roadmap_section" })}
              className="text-sm font-semibold text-orange-600 hover:underline"
            >
              Full roadmap →
            </Link>
          </div>

          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                icon: "🧠",
                title: "Intelligent item suggestions",
                desc: "Suggest relevant SSR items based on project type and context.",
              },
              {
                icon: "📐",
                title: "Quantity assistance",
                desc: "Guided quantity entry to reduce errors and missing steps.",
              },
              {
                icon: "📊",
                title: "Cost prediction & anomaly alerts",
                desc: "Identify unusually high/low values and potential omissions.",
              },
              {
                icon: "🧾",
                title: "Report readiness checks",
                desc: "Validate that required sections are complete before generation.",
              },
              {
                icon: "🔎",
                title: "Tender insights (future)",
                desc: "Compare patterns across tenders and highlight key drivers.",
              },
              {
                icon: "🛡️",
                title: "Explainable & audit-friendly AI",
                desc: "Every suggestion is reviewable and editable with traceability.",
              },
            ].map((c, idx) => (
              <div
                key={idx}
                className="rounded-2xl border border-gray-200 bg-white shadow-sm p-5 hover:shadow-md transition"
              >
                <div className="text-2xl">{c.icon}</div>
                <div className="mt-3 text-sm font-bold text-gray-900">{c.title}</div>
                <div className="mt-1 text-sm text-gray-600">{c.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PILOT CTA */}
      <section className="px-6 lg:px-20 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="text-sm font-bold text-gray-900">Interested in our AI Pilot Program?</div>
              <div className="text-sm text-gray-600 mt-1">
                Join as an early partner to shape features around real estimation workflows.
              </div>
            </div>
            <button
              type="button"
              onClick={() => requestPilot("ai_bottom")}
              className="inline-flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white px-6 py-2.5 rounded-full font-semibold text-sm shadow-sm transition"
            >
              Join Pilot / Request Demo
            </button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AI;